// https://boost.allnetru.dev/api/documentation/partner#/Events
import { createApi } from '@reduxjs/toolkit/query/react';
import {
  GetPartnerAudiencesResponse,
  GetPartnerEventByIdDataItem,
  GetPartnerEventByIdResponse,
  GetPartnerEventResponse,
  GetPartnerEventsFilterOptionsResponse,
  LeadsRequest,
  LeadsResponse,
  PartnerEventFilter,
} from './models';
import { FiltersParams } from '../../models';
import { getQueryStringFromParams } from 'lib.shared/src/utils';
import { baseQueryWithReauth } from '../../main/baseQueryWithReauth';
import { partnerEventCatalog } from '../events-catalog/partner-event-catalog.api';
const partner_event = `partner_event`;

export const partnerEvent = createApi({
  reducerPath: partner_event,
  baseQuery: baseQueryWithReauth,
  tagTypes: [partner_event],
  endpoints: (build) => ({
    getPartnerEvent: build.query<
      GetPartnerEventResponse,
      {
        filters?: PartnerEventFilter | null;
      }
    >({
      query: ({ filters }) => {
        const params = new URLSearchParams();

        if (Array.isArray(filters?.statuses)) {
          filters.statuses.forEach((id) => {
            params.append('filters[statuses][]', id.toString());
          });
        }

        if (filters?.search && filters.search.length > 2) {
          params.append('filters[search]', filters.search);
        }

        if (filters?.page) {
          params.append('page', filters.page.toString());
        }

        if (filters?.date?.from) {
          params.append('filters[date][from]', `${filters.date.from}`);
        }

        if (filters?.date?.to) {
          params.append('filters[date][to]', `${filters.date.to}`);
        }

        const queryString = params.toString();

        return {
          url: `/partner/events${queryString ? '?' + queryString : ''}`,
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: partner_event, id: 'LIST' }],
    }),
    getPartnerEventsFilterOptions: build.query<
      GetPartnerEventsFilterOptionsResponse,
      {
        filters?: FiltersParams;
        search?: string;
        page?: number;
        statuses?: string[];
      }
    >({
      query: ({ filters, search, page, statuses }) => {
        const params = new URLSearchParams();

        const relationsQuery = [
          getQueryStringFromParams(filters?.events, 'events', 'relations'),
          getQueryStringFromParams(filters?.landings, 'landings', 'relations'),
          getQueryStringFromParams(filters?.campaigns, 'campaigns', 'relations'),
          getQueryStringFromParams(filters?.partners, 'partners', 'relations'),
        ]
          .filter(Boolean)
          .join('&');

        if (relationsQuery) {
          new URLSearchParams(relationsQuery).forEach((value, key) => {
            params.append(key, value);
          });
        }

        const pageParam = page ?? filters?.page;
        if (typeof pageParam === 'number') {
          params.append('page', pageParam.toString());
        }

        statuses?.forEach((status) => {
          params.append('statuses[]', status);
        });

        if (search) {
          params.append('search', search);
        }

        const queryString = params.toString();

        return {
          url: `/partner/events/filter${queryString.length ? `?${queryString}` : ''}`,
          method: 'GET',
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: partner_event, id: 'FILTER' }],
    }),
    getPartnerEventById: build.query<GetPartnerEventByIdResponse, { id: string }>({
      query: ({ id }) => ({
        url: `/partner/events/${id}`,
        headers: {
          Accept: 'application/json',
        },
      }),
      providesTags: (_result, _error, { id }) => [{ type: partner_event, id }],
    }),
    getPartnerLeads: build.query<LeadsResponse, LeadsRequest>({
      query: ({ filters, page }) => {
        const params = new URLSearchParams();

        if (Array.isArray(filters?.campaigns)) {
          filters.campaigns.forEach((id) => {
            params.append('filters[campaigns][]', id.toString());
          });
        }
        if (Array.isArray(filters?.events)) {
          filters.events.forEach((id) => {
            params.append('filters[events][]', id.toString());
          });
        }
        if (Array.isArray(filters?.types)) {
          filters.types.forEach((id) => {
            params.append('filters[types][]', id.toString());
          });
        }

        if (filters?.search && filters.search.length > 2) {
          params.append('filters[search]', filters.search);
        }
        if (page) {
          params.append('page', page.toString());
        }
        const queryString = params.toString();

        return {
          url: `/partner/leads${queryString ? '?' + queryString : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: partner_event, id: 'LEADS' }],
    }),

    getLeadsFilterOptions: build.query<
      GetPartnerEventsFilterOptionsResponse,
      {
        filters?: FiltersParams;
        search?: string;
        page?: number;
      }
    >({
      query: ({ filters, search, page }) => {
        const params = new URLSearchParams();

        const relationsQuery = [
          getQueryStringFromParams(filters?.events, 'events', 'relations'),
          getQueryStringFromParams(filters?.landings, 'landings', 'relations'),
          getQueryStringFromParams(filters?.campaigns, 'campaigns', 'relations'),
        ]
          .filter(Boolean)
          .join('&');

        if (relationsQuery) {
          new URLSearchParams(relationsQuery).forEach((value, key) => {
            params.append(key, value);
          });
        }

        const pageParam = page ?? filters?.page;
        if (typeof pageParam === 'number') {
          params.append('page', pageParam.toString());
        }

        if (search) {
          params.append('search', search);
        }

        const queryString = params.toString();

        return {
          url: `/partner/leads/filter${queryString.length ? `?${queryString}` : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: [{ type: partner_event, id: 'LEADS_FILTER' }],
    }),

    patchPartnerEventStatusById: build.mutation<
      { data: GetPartnerEventByIdDataItem },
      { event: number; status: string; transition: number }
    >({
      query: ({ event, status, transition }) => ({
        url: `/partner/events/${event}/status/${status}`,
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: { transition },
      }),
      invalidatesTags: [{ type: partner_event, id: 'LIST' }],
      async onQueryStarted({ event }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(
            partnerEvent.util.updateQueryData(
              'getPartnerEventById',
              { id: event.toString() },
              () => data,
            ),
          );
          dispatch(partnerEventCatalog.util.invalidateTags([{ type: 'partner_event_catalog' }]));
        } catch {
          /* mutation failed — cache stays as is */
        }
      },
    }),
    getParnerAudiences: build.query<
      GetPartnerAudiencesResponse,
      { eventId: string; page?: number }
    >({
      query: ({ eventId, page }) => {
        const params = new URLSearchParams();

        if (page) {
          params.append('page', page.toString());
        }

        const queryString = params.toString();
        return {
          url: `/partner/events/${eventId}/audience-catalog${queryString ? '?' + queryString : ''}`,
          headers: {
            Accept: 'application/json',
          },
        };
      },
      providesTags: (_result, _error, { eventId }) => [
        { type: partner_event, id: `audiences-${eventId}` },
      ],
    }),
  }),
});

export const {
  useGetPartnerEventQuery,
  useGetPartnerEventByIdQuery,
  usePatchPartnerEventStatusByIdMutation,
  useGetParnerAudiencesQuery,
  useGetPartnerLeadsQuery,
  useGetPartnerEventsFilterOptionsQuery,
  useGetLeadsFilterOptionsQuery,
} = partnerEvent;
